import { Grid, makeStyles, Paper } from '@material-ui/core';
import React, {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { submit } from 'redux-form';
import UseAlertMessageState from '../../../control/alert/UseAlertMessageState';
import UseLoadingState from '../../../control/loading/UseLoadingState';
import { checkResStatusService } from '../../../utilities/ResServiceUtils';
import * as AlertMSGConstants from '../../../constants/AlertMSGConstants';
import AlertMessage from '../../../control/alert/AlertMessage';
import Loading from '../../../control/loading/Loading';
import EventCreateEditForm from './EventCreateEditForm';
import Button from '../../../control/button/Button';
import {
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import * as ApiEvent from '../../../api/ApiEvent';
import {
    sanitizeEventFormFields,
    createUpdateEventFormData,
    appendFullNameUserArray,
} from '../FormUtils';
import ModalValidateFileError from './ModalValidateFileError';
import Scrollbars from 'react-custom-scrollbars';

const useStyles = makeStyles((theme) => ({
    spacing: {
        ...theme.form.spacing,
    },
    root: {
        padding: '24px',
        overflow: 'hidden',
    },
    rootPaper: {
        minHeight: 'calc(100% - 48px)',
        margin: '24px',
        [theme.breakpoints.down('sm')]: {
            margin: '12px',
        },
        overflow: 'hidden',
        ...theme.paperPadding,
    },
    divider: {
        margin: '24px 0px',
    },
    tableCell: {
        padding: '20px',
    },
    btnCancel: {
        backgroundColor: '#808080 !important',
    },
    btnSearch: {
        width: '105px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    btnReset: {
        backgroundColor: `${theme.mainTheme.color6} !important`,
        width: '105px',
        marginRight: '8px',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginRight: '0px',
            marginBottom: '2%',
        },
    },
    btnCloseModal: {
        backgroundColor: '#808080 !important',
    },
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        maxHeight: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: theme.palette.background.paper,
        width: '50%',
        [theme.breakpoints.down('sm')]: {
            width: '75%',
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    body: {
        padding: theme.spacing(3),
    },
    label: {
        fontWeight: 'bold',
    },
    modalItemTable: {
        minWidth: 'auto',
    },
    title: {
        fontSize: '26px',
        weight: '400',
        width: '100%',
        borderBottom: '2px solid #F47B15',
    },
}));

const formId = 'EventCreateEditForm';

const EventManagementCreate = ({ authenRdc }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const alertMessageState = UseAlertMessageState();
    const loadingState = UseLoadingState();
    const location = useLocation();
    const pathArr = location.pathname.split('/');
    const lastSegment = pathArr[pathArr.length - 1];
    const history = useHistory();
    const params = useParams();

    const [preExistData, setPreExistData] = useState();
    const [validateError, setValidateError] = useState();
    const [tempDate, setTempDate] = useState({
        eventStartDate: null,
        eventEndDate: null,
        reserveStartDate: null,
        reserveEndDate: null,
    }); // state to store temporary eventStartDate to allow these date on DatePicker fn
    const [isLookupUserLoading, setIsLookupUserLoading] = useState(false);
    const isEdit = useMemo(() => {
        return lastSegment === 'edit';
    }, [lastSegment]);

    const eventId = useMemo(() => {
        if (isEdit) {
            return params.eventId;
        }
    }, [isEdit, params]);

    const fetchData = useCallback(
        async (eventId) => {
            loadingState.showLoading();
            const parameters = { userId: authenRdc.profile.uuid };
            try {
                const response = await Promise.all([
                    ApiEvent.fetchEventById(eventId, parameters),
                ]);
                setIsLookupUserLoading(true);
                ApiEvent.getLookupInternalUser().then((res) => {
                    setIsLookupUserLoading(false);
                    const userArray = appendFullNameUserArray(res.data);
                    setLookups({
                        internalUser: userArray,
                    });
                });

                if (response[0].data) {
                    const resData = sanitizeEventFormFields(response[0].data);
                    setPreExistData(resData);
                    setTempDate({
                        eventStartDate: resData.eventStartDate,
                        eventEndDate: resData.eventEndDate,
                        reserveStartDate: resData.reserveStartDate,
                        reserveEndDate: resData.reserveEndDate,
                    });
                } else {
                    alertMessageState.showAlert({
                        altMsg:
                            checkResStatusService(response) ||
                            AlertMSGConstants.SYSTEM_ERROR,
                    });
                }
                loadingState.hideLoading();
            } catch (error) {
                loadingState.hideLoading();
                if (error.status === 404) {
                    alertMessageState.showAlert({
                        altMsg: 'ไม่พบข้อมูลกิจกรรม ระบบจะนำท่านกลับเข้าสู่หน้าหลัก',
                        callbackCancel: () => {
                            history.push(`/event`);
                        },
                    });
                } else {
                    alertMessageState.showAlert({
                        altMsg: AlertMSGConstants.SYSTEM_ERROR,
                    });
                }
            }
        },
        [alertMessageState, loadingState, authenRdc, history]
    );

    const [lookups, setLookups] = useState({
        internalUser: [],
    });

    const getLookups = useCallback(async () => {
        try {
            setIsLookupUserLoading(true);
            const response = await ApiEvent.getLookupInternalUser();
            if (response.data) {
                const userArray = appendFullNameUserArray(response.data);
                setLookups({
                    internalUser: userArray,
                });
            } else {
                alertMessageState.showAlert({
                    altMsg:
                        checkResStatusService(response) ||
                        AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        } catch (error) {
            alertMessageState.showAlert({
                altMsg: AlertMSGConstants.SYSTEM_ERROR,
            });
        } finally {
            setIsLookupUserLoading(false);
        }
    }, [alertMessageState]);

    useEffect(() => {
        if (eventId && isEdit) {
            fetchData(eventId);
        } else {
            getLookups();
        }
    }, [eventId, isEdit]);

    const onCancel = useCallback(() => {
        alertMessageState.showAlert({
            altMsg: 'คุณยังไม่ได้บันทึกข้อมูลกิจกรรม หากยกเลิกข้อมูลทั้งหมดจะไม่ถูกบันทึก',
            altType: 2,
            btnConfirmName: 'ยืนยันการยกเลิก',
            btnCancelName: 'กลับสู่หน้าแก้ไขกิจกรรม',
            callbackCancel: () => alertMessageState.hideAlert(),
            callbackConfirm: () => {
                alertMessageState.hideAlert();
                history.push(`/event`);
            },
        });
    }, [history, alertMessageState]);

    const handleSaveEvent = useCallback(
        async (formValues) => {
            try {
                loadingState.showLoading();
                // sanitize fields before createing formData
                delete formValues.coordinatorTemp;
                delete formValues.updatedBy;
                delete formValues.internalReservedCount;
                delete formValues.externalReservedCount;
                const formData = await createUpdateEventFormData(
                    formValues,
                    authenRdc.profile.uuid,
                    isEdit
                );
                let isValid = true;
                if (formValues.reserveFile instanceof File) {
                    const resValidateReserveFile =
                        await ApiEvent.validateReserveFile(formData);
                    isValid = resValidateReserveFile.data.isValid;
                    if (!isValid) {
                        setValidateError({ ...resValidateReserveFile.data });
                        loadingState.hideLoading();
                        return;
                    }
                }

                const res = isEdit
                    ? await ApiEvent.updateEvent(formData, formValues.eventId)
                    : await ApiEvent.createEvent(formData);

                loadingState.hideLoading();
                if (res.data.eventId) {
                    alertMessageState.showAlert({
                        altMsg: 'บันทึกกิจกรรมสำเร็จ',
                        callbackCancel: () => {
                            history.push(`/event/${res.data.eventId}/detail`);
                            alertMessageState.hideAlert();
                        },
                    });
                }
            } catch (error) {
                loadingState.hideLoading();
                alertMessageState.showAlert({
                    altMsg:
                        Array.isArray(error.errors) && error.errors.length > 0
                            ? error.errors[0].detail
                            : AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [loadingState, alertMessageState, isEdit, authenRdc, history]
    );

    const onSubmit = useCallback(
        async (formValues) => {
            alertMessageState.showAlert({
                altTitle: 'ยืนยันการบันทึกกิจกรรม',
                altMsg: 'คุณต้องการยืนยันการบันทึกกิจกรรมนี้ใช่หรือไม่',
                altType: 2,
                callbackCancel: () => alertMessageState.hideAlert(),
                callbackConfirm: () => {
                    alertMessageState.hideAlert();
                    handleSaveEvent(formValues);
                },
            });
        },
        [alertMessageState, handleSaveEvent]
    );

    return (
        <Fragment>
            <Scrollbars universal={true}>
                <Paper className={classes.rootPaper}>
                    {isEdit ? (
                        <div className={classes.title}>แก้ไขข้อมูลกิจกรรม</div>
                    ) : (
                        <div className={classes.title}>เพิ่มข้อมูลกิจกรรม</div>
                    )}
                    <EventCreateEditForm
                        initialValues={preExistData}
                        isEdit={isEdit}
                        onSubmit={onSubmit}
                        lookups={lookups}
                        tempDate={tempDate}
                        isLookupUserLoading={isLookupUserLoading}
                        authenRdc={authenRdc}
                    />
                </Paper>
                <Grid
                    item
                    container
                    justify="center"
                    spacing={2}
                    style={{
                        marginBottom: '24px',
                        overflow: 'hidden',
                        width: '100%',
                    }}
                    gap={8}
                >
                    <Grid item>
                        <Button
                            label="ยกเลิก"
                            className={classes.btnCancel}
                            onClick={onCancel}
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            label="บันทึก"
                            onClick={() => dispatch(submit(formId))}
                            disabled={
                                isLookupUserLoading ||
                                loadingState.isShowLoading
                            }
                        />
                    </Grid>
                </Grid>
            </Scrollbars>

            <AlertMessage {...alertMessageState.altProps} />
            <Loading isShowLoading={loadingState.isShowLoading} />
            {validateError && (
                <ModalValidateFileError
                    validateError={validateError}
                    onClose={() => {
                        setValidateError(null);
                    }}
                />
            )}
        </Fragment>
    );
};

export default connect((state) => ({
    authenRdc: state.authen,
}))(EventManagementCreate);
